import { createContext, useContext } from 'react';
import type { QCIssue } from '../../core';
import type { PresetId } from './customPresets';
import type { ReportKind } from './report';

/**
 * A request to reveal a cue, consumed by the shell (which owns the route + nav).
 * `seq` changes on every intent-to-navigate move so the consumer fires once per
 * move; `cueIndex` is null for a document-level issue (nothing to jump to).
 */
export interface NavTarget {
  seq: number;
  cueIndex: number | null;
}

/**
 * QC state shared between the QC route (preset picker, run, fix-all) and the
 * Inspector (issue list, fix-one). Kept component-free so the provider file can
 * satisfy react-refresh's components-only rule.
 */
export interface QcValue {
  /** The active preset: a built-in {@link PresetName} or a `custom:${uuid}` id. */
  preset: PresetId;
  setPreset: (preset: PresetId) => void;
  /**
   * The ACTIVE issue list — issues the operator has accepted as intentional
   * (cue-forge-n3e.7) are excluded, so the counts, the review cursor, and the
   * inline grid markers all subtract them. The accepted subset is {@link accepted}.
   */
  issues: QCIssue[];
  /** Issues marked intentional and set aside (the collapsed "Accepted" group). */
  accepted: QCIssue[];
  /** Mark an issue intentional: it leaves {@link issues}, persists with the session. */
  accept: (issue: QCIssue) => void;
  /** Return a previously-accepted issue to the active list. */
  unaccept: (issue: QCIssue) => void;
  /** Whether QC has been run at least once for the current document. */
  hasRun: boolean;
  /**
   * Increments on every explicit {@link QcValue.run} (not on edit-driven
   * re-evaluation). Lets the shell react to a run — e.g. auto-open the Issues
   * drawer — without firing on every keystroke.
   */
  runCount: number;
  run: () => void;
  fixAll: () => void;
  fixOne: (issue: QCIssue) => void;
  /** Stable key of the current ("you are here") issue, or null. */
  cursorKey: string | null;
  /** The issue the cursor resolves to in the current list, or null. */
  current: QCIssue | null;
  /** Move the cursor to the next/previous issue and reveal its cue. */
  stepNext: () => void;
  stepPrev: () => void;
  /** Make a specific issue current and reveal its cue. */
  select: (issue: QCIssue) => void;
  /** Fix the current issue and advance; skip forward if it isn't fixable. */
  fixAndAdvance: () => void;
  /**
   * Accept the current issue as intentional and advance onto the successor,
   * revealing its cue (the keyboard counterpart of the per-card Accept button —
   * which, unlike this, stays put). No-op when no issue is current.
   */
  acceptAndAdvance: () => void;
  /** Latest cue-reveal request; bumped only by intent moves (not run/edit). */
  navTarget: NavTarget;
  /**
   * Export the current QC run as a downloadable, self-describing artifact — a
   * CSV (spreadsheet) or a printable HTML page (cue-forge-n3e.6). No-op until QC
   * has been run. The vendor's billable proof-of-work.
   */
  exportReport: (kind: ReportKind) => void;
  /**
   * Result of the most recent fix-one/fix-all, for the VISIBLE status line.
   * A fix can legitimately decline (e.g. re-wrapping an over-long line would
   * exceed the max-lines limit), so the UI must report "fixed" vs "couldn't
   * fix" rather than silently leaving the issue in place. Not a live region —
   * the screen-reader copy of this message lives inside {@link announce}.
   */
  fixStatus: string;
  /**
   * The ONE composed screen-reader announcement for QC intent actions
   * (cue-forge-drt.18). The provider joins each action's outcome with the
   * resulting cursor position — "Fixed X on cue 3. Issue 4 of 12: Y." — so the
   * Inspector renders a single live region and a fix-and-advance yields one
   * utterance, never two stacked ones. Background re-evaluations never set it.
   */
  announce: string;
}

export const QcContext = createContext<QcValue | null>(null);

/** Access QC state. Must be used within a QcProvider. */
export function useQc(): QcValue {
  const ctx = useContext(QcContext);
  if (!ctx) throw new Error('useQc must be used within a QcProvider');
  return ctx;
}
